import React, { memo } from 'react';
import { InfiniteQueryObserverResult } from 'react-query/types';

import ArticleOrBriefingFeedItem from '@components/Feed/ArticleOrBriefingFeedItem';
import Pagination from '@components/Pagination';
import Spinner from '@components/Spinner';
import { PartnersFeedResponse } from '@utils/backend-api/partners';

export interface FeedProps {
  pageParams: Array<number | null>;
  pages: PartnersFeedResponse[];
}

interface PartnersFeedProps {
  feed: FeedProps;
  getFeedPath: (page: number) => string;
  hasNextPage: boolean;
  isFetchingFeed: boolean;
  isLoadingFeed: boolean;
  page: number;
  setFeedPage: (
    page: number
  ) => Promise<InfiniteQueryObserverResult<PartnersFeedResponse, unknown>>;
}

interface MapPageParamsToPagesProps {
  pageParams: Array<number | null>;
  totalPages: number;
}

/**
 * Maps the pageParams to the proper paginated number.
 * useInfiniteQuery stores paginated content in an array,
 * but the array is in the order it was called, so this maps
 * that ordering to mirror the page numbers for easier usage.
 */
const mapPageParamsToPages = ({
  pageParams,
  totalPages,
}: MapPageParamsToPagesProps): Array<number | null> => {
  const mappedPages = Array(totalPages).fill(null);
  pageParams.map((val: number | null = 1, index: number) => {
    mappedPages[val] = index;
  });

  return mappedPages;
};

const PartnersFeed = ({
  feed,
  getFeedPath,
  hasNextPage,
  isFetchingFeed,
  isLoadingFeed,
  page,
  setFeedPage,
}: PartnersFeedProps): JSX.Element => {
  const transformed = mapPageParamsToPages({
    pageParams: feed?.pageParams ?? [],
    totalPages: feed?.pages[0].totalPages ?? 0,
  });
  const currentFeed = feed?.pages[transformed[page]];

  return (
    <article className="relative mx-20 mb-32 xs:mx-16 sm:mx-0">
      <div className="flex w-full items-center justify-between p-0">
        <h3 className="mr-20 whitespace-nowrap font-['Meta',Georgia,sans-serif] text-[18px] font-black text-primary-red">
          Partner Content
        </h3>
        <div className="h-2 w-full bg-primary-red"></div>
      </div>

      {(isLoadingFeed || isFetchingFeed) && (
        <div className="relative flex min-h-[500px] items-center justify-center sm:min-h-[725px] md:min-h-[800px]">
          <Spinner />
        </div>
      )}
      {currentFeed?.content ? (
        <>
          {currentFeed.content.map((val) => (
            <ArticleOrBriefingFeedItem key={val.id} articleOrBriefing={val} />
          ))}
          {(currentFeed.nextPage !== null || currentFeed.totalPages > 0) && (
            <Pagination
              paginationData={{
                nextPage: currentFeed.nextPage,
                page,
                hasNextPage,
                totalPages: currentFeed.totalPages,
              }}
              getPath={getFeedPath}
              setPage={setFeedPage}
            />
          )}
        </>
      ) : null}
    </article>
  );
};

export default memo(PartnersFeed);
